import { RegisterPage, ResetPasswordPage, SignInPage } from './AuthPages'
import { SpinTheWheel } from './SpinTheWheel'
import { ProfilePage } from './ProfilePage'

/**
 * `customer-account` — who the customer is, and everything the app keeps for them.
 *
 * Six routes over four pages. Three of them (`/moj-profil`, `/moje-objednavky`, `/moje-kupony`) are one
 * page that reads the URL to choose its section — legacy's own arrangement, kept because the header links
 * to all three.
 *
 * **The wheel and the scratch card are here, not in a `customer-gamification` module.** §0A.2 suggests one,
 * and §5 forbids it: the game is a *section of the profile page*, not a route, so a separate module would
 * have to be imported by this one — modules never import from each other. The rules that a second module
 * would genuinely share (`mayPlay`, `statusOf`) are small and stay beside the page that renders them; if a
 * game ever gets its own route, they move to `@agos/customer-core` and the module splits then.
 */
export const customerAccountModule = {
  name: 'customer-account',
  routes: [
    { path: '/prihlasenie', page: 'Prihlasenie', component: SignInPage },
    { path: '/registracia', page: 'Registracia', component: RegisterPage },
    { path: '/reset-hesla', page: 'ResetHesla', component: ResetPasswordPage },
    { path: '/moj-profil', page: 'MojProfil', component: ProfilePage },
    { path: '/moje-objednavky', page: 'MojProfil', alias: true, component: ProfilePage },
    { path: '/moje-kupony', page: 'MojProfil', alias: true, component: ProfilePage },
  ],
  // Legacy mounts the prize wheel app-wide, not on the profile: a customer is offered a spin wherever
  // they are. It draws nothing without a session and an active campaign — measured, both false today.
  banners: [{ name: 'spin-the-wheel', component: SpinTheWheel }],
} as const

export { ProfilePage, RegisterPage, ResetPasswordPage, SignInPage }
export { NotificationPreferences } from './NotificationPreferences'
export { PullToRefresh } from './PullToRefresh'
export { reorderFrom, reorderNote, type PastOrderItem, type Reorder } from './reorder'
export { SpinTheWheel }
export {
  countdownTo,
  landedOn,
  prizeNoteOf,
  wheelCampaignOf,
  wheelIsOffered,
  wheelNote,
  type WheelCampaign,
  type WheelPrizeSegment,
} from './wheel-campaign'
export {
  MIN_PASSWORD,
  MIN_PHONE,
  failureNote,
  mayRegister,
  mayResetPassword,
  maySignIn,
  newPasswordProblem,
  normaliseEmail,
  registrationProblems,
  customerUserFrom,
  sessionFrom,
  type RegistrationForm,
} from './credentials'
export {
  cancelNote,
  isUnknownStatus,
  mayCancel,
  nextLimit,
  ordersOf,
  statusLabel,
  type CustomerOrder,
  type OrderPage,
  ORDERS_PAGE_SIZE,
} from './orders'
export {
  emptyNote,
  minimumNote,
  myCouponsOf,
  sourceNote,
  statusNote,
  usable,
  worthNote,
  type CouponSource,
  type CouponStatus,
  type MyCoupon,
} from './my-coupons'
export {
  gameNote,
  mayPlay,
  prizeFrom,
  prizeNote,
  refusedUntil,
  statusOf,
  type GameKey,
  type GameState,
  type GameStatus,
  type Prize,
} from './minigame'
export {
  SPIN_DURATION_MS,
  SPIN_TURNS,
  easeOut,
  rotationAt,
  segmentArc,
  segmentUnderPointer,
  spinDestination,
  targetRotation,
  type WheelSegment,
} from './wheel'

export {
  hasReferrals,
  referralActivityOf,
  referralDate,
  referralDiscountLabel,
  referralName,
  referralNote,
  referralOfferOf,
  shareText,
  type Referral,
  type ReferralActivity,
  type ReferralOffer,
} from './referrals'
